import { Col, Modal, Row } from 'antd';
import React, { useEffect } from 'react';
import ReactHotkeys from 'react-hot-keys';
import { Breakdown, StorageInfoResponse } from '../../api/fetchStorageInfo';
import isEmpty from 'lodash/isEmpty';
import { PlanIconModalExceededOrNearLimit, PlanIconModalWithinLimit, crossIcon } from './Icons';
import { useDispatch, useSelector } from 'react-redux';
import { getIsStorageInfo, getIsStorageVisible } from '../../store/selectors/session';
import { toggleStorageModal } from '../../store/actions/session';
import CollapseItem, { CollapseItemType } from './CollapseItem';

const storageTypes = {
    MR: {
        type: 'Medical records',
        color: '#143999',
    },
    RX: {
        type: 'Prescriptions',
        color: '#215FFF',
    },
};

const FAQs: CollapseItemType[] = [
    {
        title: 'Why is my storage space being displayed now?',
        body:
            'This feature provides a complete view of your account storage. This empowers you to streamline Resources to effortlessly manage your data by understanding its footprint.',
    },
    {
        title: 'How is the storage space calculated? What type of data is stored?',
        body: (
            <span>
                Storage is calculated based on the space consumed by{' '}
                <span className="font-700">prescriptions</span> written by doctors and{' '}
                <span className="font-700">medical records</span> uploaded for patients.'
            </span>
        ),
    },
    {
        title: 'What is the current storage limit for each doctor? Does it vary per doctor?',
        body: 'This is per doctor and the storage limit is Unlimited as of TODAY.',
    },
    // {
    //     title: 'How do I manage my storage?',
    //     body:
    //         'You can manage your storage by monitoring the space used for prescriptions and medical records.',
    // },
    // {
    //     title: 'Can I download the data and move it to drive to clear my storage space?',
    //     body:
    //         "Yes, it's possible for medical records. You can download or delete them and move them to another drive to free up storage.",
    // },
    // {
    //     title: 'Will the storage space usage impact the performance of the tool?',
    //     body: "No, storage space usage will not impact the tool's performance.",
    // },
    // {
    //     title:
    //         'I have a multi-clinic, how does this work? Is storage space shared among multiple doctors or individualized?',
    //     body: 'Currently, storage is allocated per doctor.',
    // },
];

const storageDetailsInfo = [
    {
        icon: (
            <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M20.6094 8.14062L29.4219 11.8906C30.4531 12.3594 31.25 13.3438 31.25 14.5625C31.2031 19.25 29.2812 27.7344 21.2188 31.625C20.4219 32 19.5312 32 18.7344 31.625C10.6719 27.7344 8.75 19.25 8.75 14.5625C8.70312 13.3438 9.5 12.3594 10.5312 11.8906L19.3438 8.14062C19.5312 8.04688 19.7656 8 20 8C20.1875 8 20.4219 8.04688 20.6094 8.14062ZM25.2969 17.7969H25.25C25.7188 17.375 25.7188 16.6719 25.25 16.2031C24.8281 15.7812 24.125 15.7812 23.7031 16.2031L18.5 21.4531L16.2969 19.25C15.8281 18.7812 15.125 18.7812 14.7031 19.25C14.2344 19.6719 14.2344 20.375 14.7031 20.7969L17.7031 23.7969C18.125 24.2656 18.8281 24.2656 19.2969 23.7969L25.2969 17.7969Z"
                    fill="#D1EDE1"
                />
                <path
                    d="M25.2969 16.2031C25.7188 16.6719 25.7188 17.375 25.2969 17.7969L19.2969 23.7969C18.8281 24.2656 18.125 24.2656 17.7031 23.7969L14.7031 20.7969C14.2344 20.375 14.2344 19.6719 14.7031 19.25C15.125 18.7812 15.8281 18.7812 16.2969 19.25L18.5 21.4531L23.7031 16.2031C24.125 15.7812 24.8281 15.7812 25.25 16.2031H25.2969Z"
                    fill="#19A66A"
                />
            </svg>
        ),
        description:
            'Cloud storage offers reliable backup and redundancy coupled with top-tier encryption, ensuring both resilience and robust security for medical records.',
        title: 'Unified Data Security',
    },
    {
        icon: (
            <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M17.4688 10.0156L18.125 8.23438C18.1719 8.09375 18.3125 8 18.5 8C18.6406 8 18.7812 8.09375 18.8281 8.23438L19.4844 10.0156L21.2656 10.6719C21.4062 10.7188 21.5 10.8594 21.5 11C21.5 11.1875 21.4062 11.3281 21.2656 11.375L19.4844 12.0312L18.8281 13.7656C18.7812 13.9062 18.6406 14 18.5 14C18.3125 14 18.1719 13.9062 18.125 13.7656L17.4688 12.0312L15.7344 11.375C15.5938 11.3281 15.5 11.1875 15.5 11C15.5 10.8594 15.5938 10.7188 15.7344 10.6719L17.4688 10.0156ZM31.2969 13.4844L26.1406 18.6875L24.2656 16.8125L29.1875 11.8906L28.1094 10.8125L23.1875 15.7344L21.3125 13.8594L26.5156 8.70312C27.4062 7.8125 28.8125 7.8125 29.7031 8.70312L31.2969 10.2969C32.1875 11.1875 32.1875 12.5938 31.2969 13.4844ZM6.82812 13.5312L9.5 12.5L10.4844 9.875C10.5312 9.64062 10.7656 9.5 11 9.5C11.1875 9.5 11.4219 9.64062 11.4688 9.875L12.5 12.5L15.125 13.5312C15.3594 13.5781 15.5 13.8125 15.5 14C15.5 14.2344 15.3594 14.4688 15.125 14.5156L12.5 15.5L11.4688 18.1719C11.4219 18.3594 11.1875 18.5 11 18.5C10.7656 18.5 10.5312 18.3594 10.4844 18.1719L9.5 15.5L6.82812 14.5156C6.64062 14.4688 6.5 14.2344 6.5 14C6.5 13.8125 6.64062 13.5781 6.82812 13.5312ZM23.3281 25.5312L26 24.5L26.9844 21.875C27.0312 21.6406 27.2656 21.5 27.5 21.5C27.6875 21.5 27.9219 21.6406 27.9688 21.875L29 24.5L31.625 25.5312C31.8594 25.5781 32 25.8125 32 26C32 26.2344 31.8594 26.4688 31.625 26.5156L29 27.5L27.9688 30.1719C27.9219 30.3594 27.6875 30.5 27.5 30.5C27.2656 30.5 27.0312 30.3594 26.9844 30.1719L26 27.5L23.3281 26.5156C23.1406 26.4688 23 26.2344 23 26C23 25.8125 23.1406 25.5781 23.3281 25.5312Z"
                    fill="#7A9FFF"
                />
                <path
                    d="M26.1406 18.6875L13.4375 31.3438C12.5469 32.2344 11.1406 32.2344 10.25 31.3438L8.65625 29.75C7.76562 28.8594 7.76562 27.4531 8.65625 26.5625L21.3125 13.8594L26.1406 18.6875Z"
                    fill="#215FFF"
                />
            </svg>
        ),
        description:
            'Streamlined cloud interfaces enable doctors to efficiently manage and retrieve medical records from anywhere, enhancing overall practice efficiency.',
        title: 'Effortless Access & Organization',
    },
    {
        icon: (
            <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M15.5 11.75C15.5 13.1094 14.75 14.3281 13.625 15.0312C12.4531 15.6875 11 15.6875 9.875 15.0312C8.70312 14.3281 8 13.1094 8 11.75C8 10.4375 8.70312 9.21875 9.875 8.51562C11 7.85938 12.4531 7.85938 13.625 8.51562C14.75 9.21875 15.5 10.4375 15.5 11.75ZM32.75 11.75C32.75 13.1094 32 14.3281 30.875 15.0312C29.7031 15.6875 28.25 15.6875 27.125 15.0312C25.9531 14.3281 25.25 13.1094 25.25 11.75C25.25 10.4375 25.9531 9.21875 27.125 8.51562C28.25 7.85938 29.7031 7.85938 30.875 8.51562C32 9.21875 32.75 10.4375 32.75 11.75ZM9.96875 17H11.9844C12.7344 17 13.4375 17.1875 14.0938 17.4688C14 17.7969 14 18.1719 14 18.5C14 20.3281 14.75 21.9219 16.0156 23H15.9688H5.98438C5.42188 23 5 22.5781 5 22.0156C5 19.25 7.20312 17 9.96875 17ZM23.9375 23C25.2031 21.9219 26 20.3281 26 18.5C26 18.1719 25.9531 17.7969 25.9062 17.4688C26.5156 17.1875 27.2188 17 27.9688 17H29.9844C32.75 17 35 19.25 35 22.0156C35 22.5781 34.5312 23 33.9688 23H23.9844H23.9375Z"
                    fill="#F8DF9E"
                />
                <path
                    d="M20 23C18.3594 23 16.9062 22.1562 16.0625 20.75C15.2656 19.3906 15.2656 17.6562 16.0625 16.25C16.9062 14.8906 18.3594 14 20 14C21.5938 14 23.0469 14.8906 23.8906 16.25C24.6875 17.6562 24.6875 19.3906 23.8906 20.75C23.0469 22.1562 21.5938 23 20 23ZM17.2344 24.5H22.7188C26.1875 24.5 29 27.3125 29 30.7812C29 31.4844 28.4375 32 27.7344 32H12.2188C11.5156 32 11 31.4375 11 30.7812C11 27.3125 13.7656 24.5 17.2344 24.5Z"
                    fill="#EDAE0C"
                />
            </svg>
        ),
        description:
            'Cloud storage simplifies sharing medical records securely, fostering quick and efficient collaboration between healthcare professionals and patients.',
        title: 'Instant Data Sharing',
    },
];

const storageKeys = Object.keys(storageTypes);

const StorageDetailsDialog: React.FC = () => {
    const dispatch = useDispatch();
    const isVisible = useSelector(getIsStorageVisible);
    const storageResponse = useSelector(getIsStorageInfo);
    const storageInfo = storageResponse?.storage;

    const close = () => storageInfo && dispatch(toggleStorageModal(false));

    const isWithinLimit = storageInfo?.usage?.status === 'WITHIN_LIMIT';
    const limitExceeded = storageInfo?.usage?.status === 'EXCEEDED';

    const breakdown = storageInfo
        ? storageInfo.breakdown
              ?.filter(
                  (v) => v.category && v.percentage && v.amount && storageKeys.includes(v.category),
              )
              .reduce((prev: any, curr: Breakdown) => {
                  prev = {
                      ...prev,
                      [curr.category as string]: {
                          percentage: curr.percentage,
                          amount: curr.amount,
                          category: curr.category,
                          unit: curr.unit,
                      },
                  };
                  return prev;
              }, {})
        : undefined;

    const headerIcon =
        limitExceeded || storageInfo?.usage?.status === 'NEAR_LIMIT' ? (
            <PlanIconModalExceededOrNearLimit />
        ) : (
            <PlanIconModalWithinLimit />
        );

    return storageInfo ? (
        <ReactHotkeys keyName="esc" onKeyDown={close}>
            <Modal
                onCancel={close}
                closeIcon={() => null}
                footer={null}
                visible={isVisible}
                centered
                destroyOnClose
                maskClosable={true}
                bodyStyle={{ padding: 0, overflowY: 'auto', maxHeight: 'calc(100vh - 100px)' }}
                className="plan-limit-modal"
            >
                <div className="flex flex-col items-center">
                    <span
                        className="flex w-full p-8 justify-end bg-darwin-neutral_new-50 items-center"
                        onClick={close}
                    >
                        {crossIcon('w-12 h-16 mr-8 mt-8')}
                    </span>
                    <div className="flex flex-col gap-24 items-center py-24 bg-darwin-neutral_new-50 w-full">
                        <div className="flex flex-col gap-8 items-center">
                            <span className="p-4">{headerIcon}</span>
                            <p className="text-24 font-400 text-center w-full">
                                You've stored{' '}
                                <span
                                    className={`font-700 ${
                                        isWithinLimit
                                            ? 'text-darwin-neutral_new-1000'
                                            : 'text-darwin-accent-dark-yellow'
                                    }`}
                                >
                                    {storageInfo.usage?.amount} {storageInfo.usage?.unit}
                                </span>{' '}
                                of patient data.
                            </p>
                            <p className="text-14 font-400 text-darwin-neutral_new-800">
                                Your storage is shared across patient medical records and
                                prescriptions.
                            </p>
                        </div>
                        <div className="w-full px-24 flex flex-col gap-16 items-center">
                            {/* <p className="font-700 text-darwin-neutral_new-1000 text-16">
                                {limitExceeded && storageInfo.exceeded
                                    ? `${storageInfo.exceeded.amount} ${storageInfo.exceeded.unit} exceeded over free ${storageInfo.max_limit?.amount} ${storageInfo.max_limit?.unit} limit`
                                    : `${storageInfo.usage?.amount} ${storageInfo.usage?.unit} of free ${storageInfo.max_limit?.amount} ${storageInfo.max_limit?.unit} used`}
                            </p> */}
                            {breakdown && !isEmpty(breakdown) && (
                                <div className="flex flex-col gap-16 items-center w-full">
                                    <div className="h-8 rounded-16 flex max-h-8 bg-darwin-neutral_new-200 w-full">
                                        {Object.keys(breakdown).map((type, idx) => (
                                            <div
                                                className={`h-8 max-h-8 ${
                                                    Object.keys(breakdown).length === 1
                                                        ? 'rounded-16'
                                                        : idx === 0
                                                        ? 'rounded-l-16'
                                                        : idx === storageKeys.length - 1
                                                        ? 'rounded-r-16'
                                                        : ''
                                                }`}
                                                style={{
                                                    background:
                                                        storageTypes[
                                                            type as keyof typeof storageTypes
                                                        ].color,
                                                    width: `${
                                                        breakdown[type]?.percentage
                                                            ? breakdown[type].percentage
                                                            : 0
                                                    }%`,
                                                }}
                                            ></div>
                                        ))}
                                    </div>
                                    <div className="flex flex-col px-64 gap-4 w-full">
                                        {Object.keys(breakdown).map((type) => (
                                            <div className="flex justify-between items-center">
                                                <p className="flex items-center text-13 font-400 gap-8">
                                                    <span
                                                        className="rounded-full w-8 h-8"
                                                        style={{
                                                            background:
                                                                storageTypes[
                                                                    type as keyof typeof storageTypes
                                                                ].color,
                                                        }}
                                                    ></span>
                                                    {
                                                        storageTypes[
                                                            type as keyof typeof storageTypes
                                                        ].type
                                                    }
                                                </p>
                                                <p className="text-13 font-400">
                                                    {breakdown[type]?.amount
                                                        ? breakdown[type].amount
                                                        : 0}{' '}
                                                    {breakdown[type]?.unit ?? ''}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col p-24 gap-24 items-center w-full">
                        <p className="font-400 text-20">The best place for all your patient data</p>
                        <Row gutter={12} className="w-full">
                            {storageDetailsInfo.map((val) => (
                                <Col className="gutter-row" span={8}>
                                    <div className="flex flex-col items-center gap-8 px-8 py-16">
                                        <span>{val.icon}</span>
                                        <p className="font-700 text-13 text-center">{val.title}</p>
                                        <p className="font-400 text-12 text-center">
                                            {val.description}
                                        </p>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </div>
                    <div className="flex flex-col p-24 w-full gap-16">
                        <p className="text-center font-400 text-20">Frequently asked questions</p>

                        {FAQs.map((item, index) => (
                            <CollapseItem transparent={true} item={item} index={index} />
                        ))}
                    </div>
                </div>
            </Modal>
        </ReactHotkeys>
    ) : null;
};

export default StorageDetailsDialog;
